"use client";

import CallExpandBtn from "@/components/v3/CallExpandBtn";
import ContentSearchInput from "@/components/v3/ContentSearchInput";
import CustomButton from "@/components/v3/CustomButton";
import { useWordPressContentSearch } from "@/hooks/useWordPressContentSearch";
import { ROUTES } from "@/lib/routes";
import { BlogPost } from "@/types";
import { Loader2, RefreshCw } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import parse from "html-react-parser";

interface BlogsProps {
  initialPosts: BlogPost[];
  totalPages: number;
}

const Blogs = ({ initialPosts, totalPages }: BlogsProps) => {
  const {
    posts,
    searchTerm,
    setSearchTerm,
    debouncedSearch,
    isSearching,
    isLoading,
    hasMore,
    loadMore,
    isSearchActive,
  } = useWordPressContentSearch({
    initialPosts,
    totalPages,
    apiPath: "/api/posts",
    perPage: 6,
  });

  const resultSummary =
    isSearchActive && !isSearching
      ? posts.length > 0
        ? `Showing results for "${debouncedSearch}"`
        : `No results for "${debouncedSearch}"`
      : undefined;

  return (
    <div>
      <section className="container py-[80px]">
        <div className="flex flex-col gap-[60px]">
          {/* Header */}
          <div className="flex flex-col items-start justify-between gap-10 md:flex-row md:items-center">
            <h2
              className="font-crimsonText font-bold leading-none text-[#02386D]"
              style={{ fontSize: "clamp(36px, 3.23vw, 62px)" }}
            >
              Articles for Personal and
              <br />
              <span className="text-[#2857A7]">Professional Growth</span>
            </h2>

            <div className="flex w-full shrink-0 flex-col gap-5 md:w-[526px]">
              <p className="font-onest text-[20px] font-normal leading-[110%] text-[#607090]">
              Explore evidence-based insights and practical strategies designed to help you build emotional resilience, gain mental clarity, and optimize your professional performance.
              </p>
              <div className="flex items-center justify-start gap-4">
                <CallExpandBtn variant="dark" />
                <CustomButton
                  label="Get in Touch"
                  href="/contact"
                  variant="primary"
                  withIcon
                  size="lg"
                />
              </div>
            </div>
          </div>

          <ContentSearchInput
            id="blog-search"
            placeholder="Search articles..."
            value={searchTerm}
            onChange={setSearchTerm}
            isSearching={isSearching}
            resultSummary={resultSummary}
          />

          {/* Blog Cards Grid */}
          {isSearching ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
              <Loader2
                className="h-8 w-8 animate-spin text-[#006DFF]"
                aria-hidden="true"
              />
              <p className="font-onest text-[16px] text-[#607090]">
                Searching articles...
              </p>
            </div>
          ) : posts.length === 0 ? (
            <p className="text-center font-onest text-[16px] text-[#607090]">
              {isSearchActive
                ? `No blog posts found for "${debouncedSearch}".`
                : "No blog posts available at the moment."}
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-[21px] md:grid-cols-2 lg:grid-cols-3">
              {posts.map((post) => (
                <Link
                  key={post.id}
                  href={ROUTES.resources.counsellingBlogPost(post.slug)}
                  className="group relative flex flex-col overflow-hidden rounded-[16px] border border-[#E8EEF4] bg-white shadow-sm transition-shadow duration-300 hover:shadow-md"
                >
                  {/* Image Area */}
                  <div className="relative h-[250px] w-full overflow-hidden">
                    <Image
                      src={post.featuredImage || "/images/v3/blogs/blog-thumbnail.png"}
                      alt={post.imageAlt}
                      fill
                      className="object-cover transition-transform duration-700 group-hover:scale-105"
                      sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 400px"
                    />

                    {/* Gradient fade */}
                    <div
                      className="absolute bottom-0 left-0 right-0 h-[160px]"
                      style={{
                        background:
                          "linear-gradient(to top, #FFF 0%, rgba(255, 255, 255, 0.80) 40%, rgba(255, 255, 255, 0.00) 100%)",
                      }}
                    />
                  </div>
                  <div className="h-[80px] w-full bg-white"></div>

                  {/* Card Content */}
                  <div className="absolute bottom-0 flex flex-col gap-3 px-5 pb-5 pt-1">
                    <h3 className="font-crimsonText text-[22px] font-bold leading-[1.2] text-[#02386D]">
                      {parse(post.title)}
                    </h3>

                    <p className="line-clamp-2 font-onest text-[14px] font-light leading-[150%] text-[#607090]">
                      {post.excerpt}
                    </p>

                    <button
                      className="w-fit rounded-[8px] border border-[#006DFF] px-5 py-2 font-onest text-[14px] font-semibold text-[#006DFF] transition-opacity hover:opacity-80"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, #FFF 100%)",
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      Read More
                    </button>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Load More */}
          {hasMore && (
            <button
              onClick={loadMore}
              disabled={isLoading}
              className="mx-auto flex flex-row items-center justify-center gap-2 rounded-md border border-v3-primary px-6 py-3 font-onest text-[15px] font-medium text-v3-primary duration-300 hover:bg-v3-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? "Loading..." : "Load More"}{" "}
              <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
            </button>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blogs;
